'use client';

import React from 'react';

import { Sizes, cx, Color, Size } from '@UI/lib/utils';
import LoadingSpinner from '@UI/assets/LoadingSpinner';
import { getIconColors, iconSizes } from '../Icon/icon.styles';

interface LoaderProps extends React.HTMLAttributes<HTMLSpanElement> {
  loading?: boolean;
  size?: Size;
  color?: Color;
  title?: string;
}

const Loader = React.forwardRef<HTMLSpanElement, LoaderProps>(
  (props, forwardedRef) => {
    const {
      loading,
      size = Sizes.MD,
      color,
      title,
      children,
      className,
      ...other
    } = props;

    const loaderColorStyles = getIconColors('simple', color);

    if (!loading) return children;

    const Spinner = (
      <span className="flex gap-2 items-center">
        <LoadingSpinner
          className={cx(
            'animate-spin shrink-0',
            iconSizes[size].height,
            iconSizes[size].width,
            loaderColorStyles.textColor,
          )}
          style={{ transition: `width 150ms` }}
        />
        {title && <span className={`text-${size}`}>{title}</span>}
      </span>
    );

    if (children === undefined) return Spinner;

    return (
      <span
        ref={forwardedRef}
        className={cx('flex relative items-center justify-center', className)}
        {...other}
      >
        {/**
         * `display: contents` removes the content from the accessibility tree in some browsers,
         * so we force remove it with `aria-hidden`
         */}
        <span aria-hidden style={{ display: 'contents', visibility: 'hidden' }}>
          {children}
        </span>
        <span className="flex absolute items-center justify-center">
          {Spinner}
        </span>
      </span>
    );
  },
);

Loader.displayName = 'Loader';
export { Loader, type LoaderProps };
